import { LoadingOverlay, useMantineTheme } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { useMemo } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import { MantineProviders } from "./components/@custom/MantineProviders";
import { AuthProvider, RefetchProvider } from "./contexts";
import { useWindowLocationHost } from "./hooks/useWindowLocationHost";
import { ignoreUTCPlugin } from "./lib/dayjs";
import { initMonitoring } from "./lib/monitoring";
import { NotFound } from "./pages/NotFound";
import { routeTree } from "./routeTree.gen";
import { persistor, store } from "./store";

import "dayjs/locale/pt-br";
import "react-virtualized/styles.css";

dayjs.locale("pt-br");
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(ignoreUTCPlugin);
dayjs.extend(duration);
initMonitoring();

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    stepId?: number;
    checkin?: {
      updatedAt: string;
    };
  }
}

export interface RouterContext {
  store: typeof store;
  theme: ReturnType<typeof useMantineTheme>;
  state: {
    windowLocationHost: string | null;
  };
}

// Create a new router instance
export const router = createRouter({
  routeTree,
  //@ts-expect-error Se trata apenas de um erro de tipagem, os tipos faltantes são definidos em contextData
  context: {
    store,
    state: {
      windowLocationHost: null
    }
  }
});

function InnerApp() {
  const theme = useMantineTheme();

  const { windowLocationHost } = useWindowLocationHost();

  const contextData = useMemo(
    () => ({
      theme,
      state: {
        windowLocationHost
      }
    }),
    [windowLocationHost]
  );

  return (
    <Sentry.ErrorBoundary>
      <RouterProvider
        router={router}
        context={contextData}
        defaultNotFoundComponent={() => <NotFound />}
        defaultPendingComponent={() => <LoadingOverlay visible loaderProps={{ color: theme.primaryColor, size: "xl", type: "bars" }} overlayProps={{ backgroundOpacity: 1 }} />}
      />
      <TanStackRouterDevtools router={router} />
    </Sentry.ErrorBoundary>
  );
}

// Componente que conecta os contextos
function App() {
  return (
    <NuqsAdapter>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MantineProviders>
            <RefetchProvider>
              <AuthProvider>
                <InnerApp />
              </AuthProvider>
            </RefetchProvider>
          </MantineProviders>
        </PersistGate>
      </Provider>
    </NuqsAdapter>
  );
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
